import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  FileQuestion, FileText, PenTool, AlertTriangle, FileCheck, 
  CalendarDays, CheckSquare, Clock, RefreshCw, Search, Filter,
  ChevronRight, Plus, Sparkles, Building, User, AlertCircle,
  Mail, BarChart3, TrendingUp, Calendar, ArrowRight, ExternalLink,
  MoreVertical, FileDiff
} from 'lucide-react';

// KPI Card Component
function KPICard({ title, stats, icon: Icon, color, onClick }) {
  const bgColors = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-emerald-50 border-emerald-200',
    orange: 'bg-orange-50 border-orange-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200',
    slate: 'bg-slate-50 border-slate-200'
  };
  
  const iconColors = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-emerald-600 bg-emerald-100',
    orange: 'text-orange-600 bg-orange-100',
    red: 'text-red-600 bg-red-100',
    purple: 'text-purple-600 bg-purple-100',
    slate: 'text-slate-600 bg-slate-100'
  };
  
  return (
    <div 
      className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${bgColors[color]}`}
      onClick={onClick}
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold mt-1">{stats.total || stats.open || 0}</p>
        </div>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {stats.overdue > 0 && (
          <Badge className="bg-red-100 text-red-700">{stats.overdue} overdue</Badge>
        )}
        {stats.due_this_week > 0 && (
          <Badge className="bg-amber-100 text-amber-700">{stats.due_this_week} due soon</Badge>
        )}
        {stats.recent > 0 && (
          <Badge className="bg-green-100 text-green-700">{stats.recent} recent</Badge>
        )}
      </div>
    </div>
  );
}

// Urgency Badge
function UrgencyBadge({ urgency }) {
  if (!urgency || urgency.level === 'none' || urgency.level === 'normal') return null;
  
  const styles = {
    overdue: 'bg-red-100 text-red-700',
    due_soon: 'bg-orange-100 text-orange-700',
    due_this_week: 'bg-amber-100 text-amber-700'
  };
  
  return (
    <Badge className={styles[urgency.level] || 'bg-gray-100'}>
      {urgency.label} {urgency.days !== null && `(${urgency.days}d)`}
    </Badge>
  );
}

// Activity Table Row
function ActivityRow({ activity, onNavigate }) {
  return (
    <tr 
      className="hover:bg-slate-50 cursor-pointer border-b"
      onClick={() => onNavigate(activity.link)}
    >
      <td className="py-3 px-4 text-sm text-slate-500">
        {new Date(activity.date).toLocaleDateString()}
      </td>
      <td className="py-3 px-4">
        <Badge variant="outline" className="text-xs">{activity.module_type}</Badge>
      </td>
      <td className="py-3 px-4 font-mono text-sm">{activity.reference}</td>
      <td className="py-3 px-4 text-sm max-w-xs truncate">{activity.title}</td>
      <td className="py-3 px-4 text-sm">{activity.responsible_party}</td>
      <td className="py-3 px-4">
        <Badge className="text-xs">{activity.status}</Badge>
      </td>
      <td className="py-3 px-4">
        <UrgencyBadge urgency={activity.urgency} />
      </td>
      <td className="py-3 px-4 text-right">
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </td>
    </tr>
  );
}

// Urgent Item Card
function UrgentItem({ item, onNavigate, onSendReminder }) {
  return (
    <div 
      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
      onClick={() => onNavigate(item.link)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-medium text-sm">{item.reference}</p>
          <p className="text-xs text-slate-500 truncate">{item.title}</p>
          {item.contact && (
            <p className="text-xs text-slate-400 mt-1">
              <User className="inline h-3 w-3 mr-1" />
              {item.contact}
            </p>
          )}
        </div>
        <UrgencyBadge urgency={item.urgency} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
        <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 text-xs"
          onClick={(e) => { e.stopPropagation(); onSendReminder(item); }}
        >
          <Mail className="h-3 w-3 mr-1" />
          Remind
        </Button>
      </div>
    </div>
  );
}

// Task Form Dialog
function TaskFormDialog({ open, onOpenChange, projectId, users, onTaskCreated }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general',
    due_date: '',
    assigned_to_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load project teams for assignment dropdown
  useEffect(() => {
    const loadProjectTeams = async () => {
      if (!projectId || !open) return;
      
      setLoadingUsers(true);
      try {
        const projectUsers = [];
        
        // Load professional team (consultants)
        try {
          const profTeamRes = await api.teams.getProfessional(projectId);
          const profTeam = profTeamRes.data || [];
          profTeam.forEach(member => {
            if (member.primary_contact?.email) {
              projectUsers.push({
                id: member.primary_contact.user_id || member.primary_contact.email,
                name: `${member.primary_contact.name || ''} ${member.primary_contact.surname || ''}`.trim() || member.company_name,
                email: member.primary_contact.email,
                company: member.company_name,
                discipline: member.discipline,
                type: 'consultant'
              });
            }
          });
        } catch (e) {
          console.log('Could not load professional team');
        }
        
        // Load internal team (contractor team)
        try {
          const intTeamRes = await api.teams.getInternal(projectId);
          const intTeam = intTeamRes.data || [];
          intTeam.forEach(member => {
            projectUsers.push({
              id: member.user_id || member.id || member.email,
              name: member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim(),
              email: member.email,
              role: member.role,
              type: 'contractor'
            });
          });
        } catch (e) {
          console.log('Could not load internal team');
        }
        
        // Also include passed users as fallback
        const allUsers = [...projectUsers];
        users.forEach(u => {
          if (!allUsers.find(pu => pu.email === u.email)) {
            allUsers.push({ ...u, type: 'user' });
          }
        });
        
        setAssignableUsers(allUsers);
      } catch (error) {
        console.error('Error loading project teams:', error);
        setAssignableUsers(users.map(u => ({ ...u, type: 'user' })));
      } finally {
        setLoadingUsers(false);
      }
    };
    
    loadProjectTeams();
  }, [projectId, open, users]);
  
  const handleSubmit = async () => {
    if (!form.title) {
      toast.error('Title is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null
      };
      
      await api.post(`/tasks/projects/${projectId}`, payload);
      toast.success('Task created');
      onTaskCreated();
      onOpenChange(false);
      setForm({ title: '', description: '', priority: 'medium', category: 'general', due_date: '', assigned_to_id: '' });
    } catch (error) {
      toast.error('Failed to create task');
    }
    setSaving(false);
  };

  // Updated category options for consultant scope
  const categoryOptions = [
    { value: 'architectural', label: 'Architectural' },
    { value: 'structural', label: 'Structural Engineering' },
    { value: 'civil', label: 'Civil Engineering' },
    { value: 'electrical', label: 'Electrical Engineering' },
    { value: 'mechanical', label: 'Mechanical Engineering' },
    { value: 'quantity_surveying', label: 'Quantity Surveying' },
    { value: 'project_management', label: 'Project Management' },
    { value: 'health_safety', label: 'Health & Safety' },
    { value: 'contractor', label: 'Contractor' },
    { value: 'subcontractor', label: 'Subcontractor' },
    { value: 'general', label: 'General' },
    { value: 'rfi_follow_up', label: 'RFI Follow-up' },
    { value: 'si_action', label: 'SI Action' },
    { value: 'drawing_review', label: 'Drawing Review' },
    { value: 'site_visit', label: 'Site Visit' },
    { value: 'meeting_action', label: 'Meeting Action' }
  ];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="create-task-dialog">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title <span className="text-red-500">*</span></Label>
            <Input 
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              placeholder="Task title"
              data-testid="task-title-input"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea 
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categoryOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Due Date</Label>
              <Input 
                type="datetime-local"
                value={form.due_date}
                onChange={(e) => setForm({...form, due_date: e.target.value})}
                data-testid="task-due-date-input"
              />
            </div>
            <div>
              <Label>Assign To</Label>
              <Select 
                value={form.assigned_to_id || 'unassigned'} 
                onValueChange={(v) => setForm({...form, assigned_to_id: v === 'unassigned' ? '' : v})}
                disabled={loadingUsers}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingUsers ? "Loading..." : "Select assignee"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {/* Consultants Group */}
                  {assignableUsers.filter(u => u.type === 'consultant').length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Consultants</div>
                      {assignableUsers.filter(u => u.type === 'consultant').map(u => (
                        <SelectItem key={u.id || u.email} value={u.id || u.email}>
                          {u.name} {u.discipline ? `(${u.discipline})` : ''}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {/* Contractor Team Group */}
                  {assignableUsers.filter(u => u.type === 'contractor').length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Contractor Team</div>
                      {assignableUsers.filter(u => u.type === 'contractor').map(u => (
                        <SelectItem key={u.id || u.email} value={u.id || u.email}>
                          {u.name || u.email} {u.role ? `(${u.role})` : ''}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {/* Other Users Group */}
                  {assignableUsers.filter(u => u.type === 'user').length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">Other Users</div>
                      {assignableUsers.filter(u => u.type === 'user').map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="create-task-submit">
            {saving ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Simple Calendar Component
function SimpleCalendar({ items, onItemClick, currentMonth, onMonthChange }) {
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  
  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  
  const getItemsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(item => item.date?.startsWith(dateStr));
  };
  
  const colorMap = {
    rfi: 'bg-blue-500',
    task: 'bg-amber-500',
    notice: 'bg-orange-500',
    claim: 'bg-purple-500',
    milestone: 'bg-indigo-500'
  };
  
  return (
    <div className="border rounded-lg p-4" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => onMonthChange(-1)}>
          &lt; Prev
        </Button>
        <h3 className="font-semibold">
          {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h3>
        <Button variant="ghost" size="sm" onClick={() => onMonthChange(1)}>
          Next &gt;
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">{day}</div>
        ))}
        {days.map((day, idx) => {
          const dayItems = getItemsForDay(day);
          return (
            <div 
              key={idx} 
              className={`min-h-16 p-1 border rounded ${day ? 'bg-white' : 'bg-slate-50'}`}
            >
              {day && (
                <>
                  <p className="text-xs font-medium">{day}</p>
                  <div className="mt-1 space-y-0.5">
                    {dayItems.slice(0, 3).map((item, i) => (
                      <div 
                        key={i}
                        className={`text-xs px-1 py-0.5 rounded text-white truncate cursor-pointer ${colorMap[item.type] || 'bg-slate-500'}`}
                        onClick={() => onItemClick(item)}
                        title={item.title}
                      >
                        {item.title.substring(0, 15)}
                      </div>
                    ))}
                    {dayItems.length > 3 && (
                      <p className="text-xs text-slate-400">+{dayItems.length - 3} more</p>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main Dashboard Component
export default function ProjectSummaryDashboard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [urgentItems, setUrgentItems] = useState({ contractor: [], consultant: [], subcontractor: [] });
  const [calendarItems, setCalendarItems] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [users, setUsers] = useState([]);
  
  const [periodDays, setPeriodDays] = useState(7);
  const [partyFilter, setPartyFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  useEffect(() => {
    loadDashboardData();
  }, [projectId, periodDays]);
  
  useEffect(() => {
    if (activeTab === 'calendar') {
      loadCalendarItems();
    }
  }, [activeTab, currentMonth]);
  
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [summaryRes, activityRes, urgentRes] = await Promise.all([
        api.get(`/dashboard/projects/${projectId}/summary?days=${periodDays}`),
        api.get(`/dashboard/projects/${projectId}/recent-activity?days=${periodDays}`),
        api.get(`/dashboard/projects/${projectId}/urgent-deadlines`)
      ]);
      
      setSummary(summaryRes.data);
      setActivities(activityRes.data.activities);
      setUrgentItems(urgentRes.data);
      
      // Load project team members (accessible to all users)
      try {
        const usersRes = await api.get(`/users/project/${projectId}`);
        setUsers(usersRes.data || []);
      } catch (userErr) {
        console.log('Could not load project users');
        setUsers([]);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  };
  
  const loadCalendarItems = async () => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    try {
      const res = await api.get(`/dashboard/projects/${projectId}/calendar-items`, {
        params: {
          start_date: start.toISOString().split('T')[0],
          end_date: end.toISOString().split('T')[0]
        }
      });
      setCalendarItems(res.data.items);
    } catch (error) {
      console.error('Failed to load calendar items');
    }
  };
  
  const generateAISummary = async () => {
    setGeneratingAI(true);
    try {
      const res = await api.post(`/dashboard/projects/${projectId}/generate-summary?days=${periodDays}`);
      setAiSummary(res.data);
      toast.success('Summary generated');
    } catch (error) {
      toast.error('Failed to generate summary');
    }
    setGeneratingAI(false);
  };
  
  const handleSendReminder = async (item) => {
    toast.success(`Reminder logged for ${item.reference}`);
    // In production, this would trigger an email
  };
  
  const filteredActivities = useMemo(() => {
    let filtered = activities;
    
    if (partyFilter) {
      filtered = filtered.filter(a => a.responsible_party.toLowerCase().includes(partyFilter.toLowerCase()));
    }
    if (moduleFilter) {
      filtered = filtered.filter(a => a.module_type.toLowerCase() === moduleFilter.toLowerCase());
    }
    if (searchQuery) {
      filtered = filtered.filter(a => 
        a.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.title?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [activities, partyFilter, moduleFilter, searchQuery]);
  
  const handleMonthChange = (delta) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1));
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="project-summary-dashboard">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <Button
            onClick={() => navigate(`/projects/${projectId}`)}
            variant="ghost"
            className="mb-2 -ml-2 text-slate-600"
          >
            ← Back to Project
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight truncate">PROJECT SUMMARY</h1>
          <p className="text-sm text-slate-500 truncate">{summary?.project?.name} - {summary?.project?.contract_type}</p>
        </div>
        
        {/* Desktop Controls */}
        <div className="hidden sm:flex items-center gap-3">
          <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadDashboardData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowTaskDialog(true)} data-testid="create-task-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
        </div>
        
        {/* Mobile Controls - Dropdown */}
        <div className="flex sm:hidden items-center gap-2">
          <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={loadDashboardData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowTaskDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Task
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard 
          title="RFIs" 
          stats={summary?.kpis?.rfis || {}}
          icon={FileQuestion}
          color="blue"
          onClick={() => navigate(`/projects/${projectId}?tab=rfis`)}
        />
        <KPICard 
          title="Site Instructions" 
          stats={summary?.kpis?.site_instructions || {}}
          icon={FileText}
          color="orange"
          onClick={() => navigate(`/projects/${projectId}?tab=si`)}
        />
        <KPICard 
          title="Drawings" 
          stats={summary?.kpis?.drawings || {}}
          icon={PenTool}
          color="green"
          onClick={() => navigate(`/projects/${projectId}?tab=drawings`)}
        />
        <KPICard 
          title="Delay Events" 
          stats={summary?.kpis?.delay_events || {}}
          icon={AlertTriangle}
          color="red"
          onClick={() => navigate(`/projects/${projectId}?tab=events`)}
        />
        <KPICard 
          title="Notices" 
          stats={summary?.kpis?.notices || {}}
          icon={FileCheck}
          color="purple"
          onClick={() => navigate(`/projects/${projectId}?tab=notices`)}
        />
        <KPICard 
          title="Claims" 
          stats={summary?.kpis?.claims || {}}
          icon={BarChart3}
          color="slate"
          onClick={() => navigate(`/projects/${projectId}?tab=claims`)}
        />
        <KPICard 
          title="Programs" 
          stats={summary?.kpis?.programs || {}}
          icon={CalendarDays}
          color="blue"
          onClick={() => navigate(`/projects/${projectId}?tab=programs`)}
        />
        <KPICard 
          title="Tasks" 
          stats={summary?.kpis?.tasks || {}}
          icon={CheckSquare}
          color="green"
          onClick={() => setActiveTab('tasks')}
        />
        <KPICard 
          title="Variation Orders" 
          stats={summary?.kpis?.variation_orders || {}}
          icon={FileDiff}
          color="purple"
          onClick={() => navigate(`/projects/${projectId}/vos`)}
        />
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: Dropdown */}
        <div className="sm:hidden mb-4">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select view" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overview">Overview</SelectItem>
              <SelectItem value="activity">Recent Activity</SelectItem>
              <SelectItem value="urgent">Urgent Deadlines</SelectItem>
              <SelectItem value="calendar">Calendar</SelectItem>
              <SelectItem value="tasks">Tasks</SelectItem>
              <SelectItem value="ai-summary">AI Summary</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Desktop: Tab List */}
        <div className="hidden sm:block">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activity">Recent Activity</TabsTrigger>
            <TabsTrigger value="urgent">Urgent Deadlines</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="ai-summary">AI Summary</TabsTrigger>
          </TabsList>
        </div>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Activity Preview */}
            <div className="lg:col-span-2 border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Recent Activity</h3>
                <Button variant="link" size="sm" onClick={() => setActiveTab('activity')}>
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="space-y-2">
                {activities.slice(0, 5).map((activity, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer"
                    onClick={() => navigate(activity.link)}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{activity.module_type}</Badge>
                      <span className="font-mono text-sm">{activity.reference}</span>
                      <span className="text-sm text-slate-600 truncate max-w-xs">{activity.title}</span>
                    </div>
                    <UrgencyBadge urgency={activity.urgency} />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Urgent Items Summary */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-red-600">
                  <AlertCircle className="inline h-4 w-4 mr-1" />
                  Urgent Items ({urgentItems.totals?.total || 0})
                </h3>
                <Button variant="link" size="sm" onClick={() => setActiveTab('urgent')}>
                  View All
                </Button>
              </div>
              <div className="space-y-2">
                {[...urgentItems.contractor, ...urgentItems.consultant].slice(0, 5).map((item, idx) => (
                  <div 
                    key={idx}
                    className="p-2 border rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(item.link)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{item.reference}</span>
                      <UrgencyBadge urgency={item.urgency} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">{item.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Recent Activity Tab */}
        <TabsContent value="activity">
          <div className="border rounded-lg">
            <div className="p-4 border-b flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search reference or title..." 
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="activity-search"
                />
              </div>
              <Select value={moduleFilter || "all"} onValueChange={(v) => setModuleFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Modules" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  <SelectItem value="rfi">RFIs</SelectItem>
                  <SelectItem value="si">Site Instructions</SelectItem>
                  <SelectItem value="drawing">Drawings</SelectItem>
                  <SelectItem value="event">Delay Events</SelectItem>
                  <SelectItem value="notice">Notices</SelectItem>
                  <SelectItem value="claim">Claims</SelectItem>
                </SelectContent>
              </Select>
              <Select value={partyFilter || "all"} onValueChange={(v) => setPartyFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Parties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Parties</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Module</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Reference</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Title</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Party</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Urgency</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((activity, idx) => (
                    <ActivityRow key={idx} activity={activity} onNavigate={navigate} />
                  ))}
                </tbody>
              </table>
              {filteredActivities.length === 0 && (
                <p className="text-center py-8 text-slate-500">No activities found</p>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* Urgent Deadlines Tab */}
        <TabsContent value="urgent">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center">
                <Building className="h-4 w-4 mr-2" />
                Contractor ({urgentItems.contractor.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {urgentItems.contractor.map((item, idx) => (
                  <UrgentItem 
                    key={idx} 
                    item={item} 
                    onNavigate={navigate}
                    onSendReminder={handleSendReminder}
                  />
                ))}
                {urgentItems.contractor.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No urgent items</p>
                )}
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center">
                <User className="h-4 w-4 mr-2" />
                Consultant ({urgentItems.consultant.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {urgentItems.consultant.map((item, idx) => (
                  <UrgentItem 
                    key={idx} 
                    item={item} 
                    onNavigate={navigate}
                    onSendReminder={handleSendReminder}
                  />
                ))}
                {urgentItems.consultant.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No urgent items</p>
                )}
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center">
                <Building className="h-4 w-4 mr-2" />
                Subcontractor ({urgentItems.subcontractor.length})
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {urgentItems.subcontractor.map((item, idx) => (
                  <UrgentItem 
                    key={idx} 
                    item={item} 
                    onNavigate={navigate}
                    onSendReminder={handleSendReminder}
                  />
                ))}
                {urgentItems.subcontractor.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No urgent items</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <SimpleCalendar 
            items={calendarItems}
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
            onItemClick={(item) => navigate(item.link)}
          />
          <div className="mt-4 flex gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded" />
              <span className="text-sm">RFIs</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500 rounded" />
              <span className="text-sm">Tasks</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-500 rounded" />
              <span className="text-sm">Notices</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-purple-500 rounded" />
              <span className="text-sm">Claims</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-indigo-500 rounded" />
              <span className="text-sm">Milestones</span>
            </div>
          </div>
        </TabsContent>
        
        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <TasksPanel projectId={projectId} users={users} onRefresh={loadDashboardData} />
        </TabsContent>
        
        {/* AI Summary Tab */}
        <TabsContent value="ai-summary">
          <div className="border rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg">
                <Sparkles className="inline h-5 w-5 mr-2 text-purple-500" />
                AI-Generated Project Summary
              </h3>
              <Button onClick={generateAISummary} disabled={generatingAI}>
                {generatingAI ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Summary
                  </>
                )}
              </Button>
            </div>
            
            {aiSummary ? (
              <div className="space-y-6">
                <div className="prose max-w-none">
                  <div className="whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                    {aiSummary.narrative}
                  </div>
                </div>
                
                {aiSummary.references?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">References</h4>
                    <div className="flex flex-wrap gap-2">
                      {aiSummary.references.map((ref, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline"
                          className="cursor-pointer hover:bg-slate-100"
                          onClick={() => navigate(ref.link)}
                        >
                          {ref.code}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-slate-500">
                  Generated: {new Date(aiSummary.generated_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Sparkles className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Click "Generate Summary" to create an AI-powered project overview</p>
                <p className="text-sm mt-2">Includes key events, blockers, risks, and upcoming deadlines</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Task Dialog */}
      <TaskFormDialog 
        open={showTaskDialog}
        onOpenChange={setShowTaskDialog}
        projectId={projectId}
        users={users}
        onTaskCreated={loadDashboardData}
      />
    </div>
    </DashboardLayout>
  );
}

// Tasks Panel Component
function TasksPanel({ projectId, users, onRefresh }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();
  
  useEffect(() => {
    loadTasks();
  }, [projectId, statusFilter]);
  
  const loadTasks = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get(`/tasks/projects/${projectId}${params}`);
      setTasks(res.data.tasks);
      setStats(res.data.stats);
    } catch (error) {
      toast.error('Failed to load tasks');
    }
    setLoading(false);
  };
  
  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/projects/${projectId}/${taskId}`, { status: newStatus });
      toast.success('Task updated');
      loadTasks();
      onRefresh();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };
  
  const priorityColors = {
    low: 'bg-slate-100 text-slate-600',
    medium: 'bg-blue-100 text-blue-600',
    high: 'bg-orange-100 text-orange-600',
    urgent: 'bg-red-100 text-red-700'
  };
  
  const statusColors = {
    open: 'bg-slate-100 text-slate-600',
    in_progress: 'bg-blue-100 text-blue-600',
    completed: 'bg-green-100 text-green-600',
    cancelled: 'bg-red-100 text-red-600'
  };
  
  if (loading) {
    return <div className="text-center py-8"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Badge className="bg-slate-100 text-slate-700">Total: {stats.total}</Badge>
          <Badge className="bg-blue-100 text-blue-700">Open: {stats.open}</Badge>
          <Badge className="bg-amber-100 text-amber-700">In Progress: {stats.in_progress}</Badge>
          <Badge className="bg-green-100 text-green-700">Completed: {stats.completed}</Badge>
          {stats.overdue > 0 && <Badge className="bg-red-100 text-red-700">Overdue: {stats.overdue}</Badge>}
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        {tasks.map((task) => (
          <div key={task.id} className="border rounded-lg p-4 hover:bg-slate-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{task.title}</h4>
                  <Badge className={priorityColors[task.priority]}>{task.priority}</Badge>
                  <Badge className={statusColors[task.status]}>{task.status.replace('_', ' ')}</Badge>
                </div>
                {task.description && (
                  <p className="text-sm text-slate-500 mt-1">{task.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  {task.due_date && (
                    <span>
                      <Clock className="inline h-3 w-3 mr-1" />
                      Due: {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                  {task.assigned_to_name && (
                    <span>
                      <User className="inline h-3 w-3 mr-1" />
                      {task.assigned_to_name}
                    </span>
                  )}
                  <span>Created by: {task.assigned_by_name}</span>
                </div>
              </div>
              <div className="flex gap-1">
                {task.status === 'open' && (
                  <Button size="sm" variant="outline" onClick={() => updateTaskStatus(task.id, 'in_progress')}>
                    Start
                  </Button>
                )}
                {task.status === 'in_progress' && (
                  <Button size="sm" variant="outline" onClick={() => updateTaskStatus(task.id, 'completed')}>
                    Complete
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
        {tasks.length === 0 && (
          <p className="text-center py-8 text-slate-500">No tasks found</p>
        )}
      </div>
    </div>
  );
}
